// #141 Bekleyen Siparişler paneli — araca yüklenmemiş tüm siparişler.
// Kompakt dar satırlar (1 ekrana çok kayıt sığsın): müşteri + mahalle + kg/₺ + Fat./İrs.
// Üstte genel arama (mahalle/kod/ünvan/kısa ad/adres/sokak/temsilci). Drop target:
// bir araçtan çıkarılan / buraya bırakılan sipariş Bekleyen'e döner.
import { useMemo, useState } from 'react';
import { DocTypeChip } from './OrderCard';
import { fmtNumber, fmtMoney, filterOrders } from './shipmentPlanningUtils';

export default function PendingPanel({
  orders = [], selectedOrderId, onSelectOrder, onDocTypeChange,
  onDragStart, onDragEnd, onDropHere,
  checkedIds, onToggleChecked, onToggleAll,
}) {
  const [over, setOver] = useState(false);
  const [query, setQuery] = useState('');
  const accent = '#0073ea';

  const filtered = useMemo(() => filterOrders(orders, query), [orders, query]);
  const totalWeight = orders.reduce((s, o) => s + (Number(o.totalWeight) || 0), 0);
  const totalAmount = orders.reduce((s, o) => s + (Number(o.netAmount) || 0), 0);

  // "Tümünü seç" görünürdeki (aramayla süzülmüş) siparişler üzerinden çalışır.
  const checkedHere = filtered.filter((o) => checkedIds?.has(o.orderId)).length;
  const allChecked = filtered.length > 0 && checkedHere === filtered.length;

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); onDropHere?.(); }}
      style={{
        display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%',
        background: over ? 'rgba(0,115,234,0.05)' : 'var(--grid-canvas,#f6f8fc)',
        border: `1.5px ${over ? 'dashed' : 'solid'} ${over ? accent : 'var(--border-color,#e5e7eb)'}`,
        borderRadius: 16, overflow: 'hidden',
      }}
    >
      {/* Başlık + özet */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderBottom: '1px solid var(--border-color,#e5e7eb)', background: 'var(--bg-card,#fff)' }}>
        <span aria-hidden="true" style={{ fontSize: 15 }}>📋</span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 800, fontSize: 13.5, color: 'var(--text-primary,#111827)' }}>Bekleyen Siparişler</div>
          <div style={{ fontSize: 10.5, color: 'var(--text-secondary,#94a3b8)', fontWeight: 600 }}>Araca yüklenmemiş = planlanmamış</div>
        </div>
        <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 800, color: accent, background: `${accent}1a`, border: `1px solid ${accent}33`, borderRadius: 999, padding: '2px 9px' }}>{orders.length}</span>
      </div>

      {/* Arama */}
      <div style={{ padding: '8px 10px', borderBottom: '1px dashed var(--border-color,#e5e7eb)', background: 'var(--bg-card,#fff)' }}>
        <div style={{ position: 'relative' }}>
          <span aria-hidden="true" style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: '#94a3b8' }}>🔍</span>
          <input
            value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Mahalle, kod, ünvan, adres, temsilci…"
            style={{ width: '100%', boxSizing: 'border-box', padding: '7px 26px 7px 28px', borderRadius: 9, border: '1px solid var(--border-color,#cbd5e1)', fontSize: 12, background: 'var(--input-bg,#fff)', color: 'var(--text-primary,#1f2937)' }}
          />
          {query && (
            <button type="button" onClick={() => setQuery('')} aria-label="Temizle"
              style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'transparent', cursor: 'pointer', color: '#94a3b8', fontSize: 15, lineHeight: 1 }}>×</button>
          )}
        </div>
        {orders.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 7, alignItems: 'center' }}>
            <label style={selectAllLabel} title="Görünen siparişlerin tamamını seç">
              <input type="checkbox" checked={allChecked}
                onChange={(e) => onToggleAll?.(filtered, e.target.checked)} />
              Tümü
            </label>
            <Chip color="#1f2937">{fmtNumber(totalWeight)} kg</Chip>
            <Chip color="#6b6f1e">{fmtMoney(totalAmount)} ₺</Chip>
            {query && <Chip color="#0073ea">{filtered.length} eşleşme</Chip>}
            {checkedHere > 0 && <Chip color="#7a3cff">{checkedHere} seçili</Chip>}
          </div>
        )}
      </div>

      {/* Kompakt liste */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 8, display: 'flex', flexDirection: 'column', gap: 5, minHeight: 120 }}>
        {orders.length === 0 ? (
          <Empty>Kriter girip <b>Listele</b>'ye basın; gelen siparişler burada toplanır.</Empty>
        ) : filtered.length === 0 ? (
          <Empty>Aramayla eşleşen sipariş yok.</Empty>
        ) : filtered.map((o) => (
          <CompactPendingRow
            key={o.orderId}
            order={o}
            selected={o.orderId === selectedOrderId}
            checked={!!checkedIds?.has(o.orderId)}
            onToggleChecked={onToggleChecked}
            onSelect={onSelectOrder}
            onDocTypeChange={onDocTypeChange}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
          />
        ))}
      </div>
    </div>
  );
}

function CompactPendingRow({ order, selected, checked, onToggleChecked, onSelect, onDocTypeChange, onDragStart, onDragEnd }) {
  return (
    <div
      draggable
      onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; onDragStart?.(order); }}
      onDragEnd={() => onDragEnd?.()}
      onClick={() => onSelect?.(order)}
      style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '6px 9px',
        border: `1px solid ${checked ? '#7a3cff' : selected ? 'var(--accent)' : 'var(--border-color,#e5e7eb)'}`,
        borderRadius: 9,
        background: checked ? 'rgba(122,60,255,0.06)' : selected ? 'rgba(0,115,234,0.06)' : 'var(--bg-card,#fff)',
        cursor: 'grab', userSelect: 'none',
        boxShadow: selected ? '0 0 0 2px rgba(0,115,234,0.16)' : 'none',
      }}
    >
      <input type="checkbox" checked={checked} onClick={(e) => e.stopPropagation()}
        onChange={() => onToggleChecked?.(order)}
        aria-label="Siparişi seç" style={{ flexShrink: 0, cursor: 'pointer' }} />
      <span aria-hidden="true" style={{ color: '#cbd5e1', fontSize: 12, flexShrink: 0 }}>⋮⋮</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 12.5, color: 'var(--text-primary,#0f172a)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {order.customerName}
        </div>
        <div style={{ fontSize: 10.5, color: 'var(--text-secondary,#94a3b8)', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {[order.customerCode, order.neighborhood].filter(Boolean).join(' · ') || '—'}
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3, flexShrink: 0 }}>
        <span style={{ fontSize: 10, fontWeight: 800, color: '#fff', background: '#1f2937', borderRadius: 999, padding: '1px 7px', whiteSpace: 'nowrap' }}>{fmtNumber(order.totalWeight)} kg</span>
        <DocTypeChip value={order.documentType} onToggle={(v) => onDocTypeChange?.(order, v)} />
      </div>
    </div>
  );
}

const selectAllLabel = {
  display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10.5, fontWeight: 800,
  color: '#475569', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
};

function Chip({ children, color = '#64748b' }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10.5, fontWeight: 800, color, background: `${color}16`, border: `1px solid ${color}30`, borderRadius: 999, padding: '2px 8px', whiteSpace: 'nowrap' }}>{children}</span>
  );
}

function Empty({ children }) {
  return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', color: 'var(--text-secondary,#94a3b8)', fontSize: 12, padding: 16, lineHeight: 1.6 }}>
      {children}
    </div>
  );
}
